// src/pages/VideoStreams/VideoStreamsPage.jsx
// АРМ модуля «Видеопоток» — сторона ввода (административная панель).
// Стек: React + Ant Design v4. Основание: СТПО СЦ, UC.12.02, таблицы 71–72,
// RQ.21.01–RQ.21.06. Только создание (без правки/удаления) — по сценарию таблицы 71.
//
// Подписи интерфейса — из общего i18next-словаря, префикс videoStreams.* (RU/KK).
// Значения записей не переводятся (вариант А, отклонение №2).

import React, { useCallback, useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Upload,
  notification,
  message,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  UploadOutlined,
  VideoCameraOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { listVideoStreams, createVideoStream, ApiError } from './videoStreamsApi';
import BasicLayout from '../../components/layout/BasicLayout';

// ISO (YYYY-MM-DD) → ДД.ММ.ГГГГ либо «—» (без импорта moment)
const fmtDate = (d) => (d ? `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}` : '—');

// Upload → значение поля формы (массив fileList)
const normFile = (e) => (Array.isArray(e) ? e : e && e.fileList);

// Соответствие имён полей сервера (422) ↔ имён Form.Item
const FIELD_MAP = {
  source_name: 'source_name',
  address: 'address',
  location: 'location',
  video: 'video',
  plan_date: 'plan_date',
  fact_date: 'fact_date',
};

export default function VideoStreamsPage() {
  const { t } = useTranslation();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  /* ------------------------- Загрузка перечня (RQ.21.01) ------------------------- */
  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const data = await listVideoStreams();
      setRows(data);
    } catch (e) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* ------------------------------- Открыть форму ------------------------------ */
  const openModal = () => {
    form.resetFields();
    setModalOpen(true);
  };

  // АС1 UC.12.02 — закрытие без сохранения (Отмена / крестик / Esc / клик по маске)
  const closeModal = () => {
    if (submitting) return;
    setModalOpen(false);
    form.resetFields();
  };

  /* ------------------------------- Сохранение ------------------------------- */
  const onFinish = async (values) => {
    // АС2: обязательные поля 1–3 и файл проверены правилами Form до сюда.
    const fd = new FormData();
    fd.append('source_name', (values.source_name || '').trim());
    fd.append('address', (values.address || '').trim());
    fd.append('location', (values.location || '').trim());
    fd.append('plan_date', values.plan_date ? values.plan_date.format('YYYY-MM-DD') : '');
    fd.append('fact_date', values.fact_date ? values.fact_date.format('YYYY-MM-DD') : '');
    const file = values.video && values.video[0] && values.video[0].originFileObj;
    if (file) fd.append('video', file);

    setSubmitting(true);
    try {
      const created = await createVideoStream(fd); // POST /api/video-streams (RQ.21.03)
      // 201: добавить запись из тела ответа (без повторного запроса), закрыть форму
      setRows((prev) => [created, ...prev]);
      setModalOpen(false);
      form.resetFields();
      // Подтверждение со ссылкой на журнал действий (шаги 8–9 ОС)
      notification.success({
        message: t('videoStreams.toastOk'),
        description: t('videoStreams.toastSub'),
        placement: 'bottomRight',
      });
    } catch (err) {
      handleSaveError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveError = (err) => {
    const status = err instanceof ApiError ? err.status : 0;

    // 422 — подсветить те же поля по данным сервера (5.5)
    if (status === 422 && err.body && err.body.fields) {
      const fields = err.body.fields;
      const names = Array.isArray(fields) ? fields : Object.keys(fields);
      form.setFields(
        names.map((sf) => {
          const name = FIELD_MAP[sf] || sf;
          const serverMsg = !Array.isArray(fields) && fields[sf] ? fields[sf] : null;
          const fallback =
            name === 'video' ? t('videoStreams.reqMsgFile') : t('videoStreams.reqMsg');
          return { name, errors: [serverMsg || fallback] };
        })
      );
      return;
    }
    // Ошибки файла — на поле «Видеофайл»
    if (status === 413) {
      form.setFields([{ name: 'video', errors: [t('videoStreams.errFile413')] }]);
      return;
    }
    if (status === 415) {
      form.setFields([{ name: 'video', errors: [t('videoStreams.errFile415')] }]);
      return;
    }
    // 500 / сеть — форма остаётся открытой, показываем сообщение с возможностью повторить
    message.error(t('videoStreams.errSave'));
  };

  /* -------------------------------- Колонки --------------------------------- */
  const columns = [
    { title: t('videoStreams.colSrc'), dataIndex: 'source_name', key: 'source_name' },
    { title: t('videoStreams.colAddr'), dataIndex: 'address', key: 'address' },
    { title: t('videoStreams.colLoc'), dataIndex: 'location', key: 'location' },
    {
      title: t('videoStreams.colFile'),
      key: 'video',
      render: (_, r) => (
        <a
          href={(r.video && r.video.url) || undefined}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <VideoCameraOutlined style={{ color: '#2f80c9' }} />
          {r.video && r.video.file_name}
        </a>
      ),
    },
    {
      title: t('videoStreams.colPlan'),
      dataIndex: 'plan_date',
      key: 'plan_date',
      align: 'right',
      width: 150,
      render: (d) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtDate(d)}</span>,
    },
    {
      title: t('videoStreams.colFact'),
      dataIndex: 'fact_date',
      key: 'fact_date',
      align: 'right',
      width: 150,
      render: (d) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtDate(d)}</span>,
    },
    // Столбца действий нет: по сценарию таблицы 71 доступно только создание (create-only).
  ];

  const emptyNode = (
    <div style={{ padding: '32px 16px', textAlign: 'center' }}>
      <InboxOutlined style={{ fontSize: 30, color: '#c3cdda' }} />
      <div style={{ fontWeight: 600, color: '#1d2734', marginTop: 8 }}>
        {t('videoStreams.emptyTitle')}
      </div>
      <div style={{ color: '#8a94a6', fontSize: 13, maxWidth: 420, margin: '4px auto 0' }}>
        {t('videoStreams.emptyText')}
      </div>
    </div>
  );

  return (
    <BasicLayout>
      <div style={{ padding: '24px 30px 40px' }}>
        {/* Заголовок подраздела + кнопка «Создать» */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
          }}
        >
          <h1 style={{ fontSize: 21, fontWeight: 500, color: '#1d2734', margin: 0 }}>
            {t('videoStreams.title')}
          </h1>
          <Button type="primary" icon={<PlusOutlined />} onClick={openModal}>
            {t('videoStreams.create')}
          </Button>
        </div>

        {loadError ? (
          <Alert
            type="error"
            showIcon
            message={t('videoStreams.errLoad')}
            action={
              <Button size="small" onClick={load}>
                {t('videoStreams.retry')}
              </Button>
            }
          />
        ) : (
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={rows}
            locale={{ emptyText: emptyNode }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              size: 'default',
            }}
          />
        )}

        {/* Модальная форма создания (таблица 72, RQ.21.02) */}
        <Modal
          title={t('videoStreams.mTitle')}
          visible={modalOpen}
          onCancel={closeModal}
          maskClosable={!submitting}
          keyboard={!submitting}
          width={640}
          footer={[
            <Button key="cancel" onClick={closeModal} disabled={submitting}>
              {t('videoStreams.cancel')}
            </Button>,
            <Button key="save" type="primary" loading={submitting} onClick={() => form.submit()}>
              {t('videoStreams.save')}
            </Button>,
          ]}
        >
          <Form form={form} layout="vertical" requiredMark scrollToFirstError onFinish={onFinish}>
            {/* 1. Наименование источника */}
            <Form.Item
              name="source_name"
              label={t('videoStreams.fSource')}
              rules={[{ required: true, whitespace: true, message: t('videoStreams.reqMsg') }]}
            >
              <Input maxLength={250} showCount placeholder={t('videoStreams.phSource')} />
            </Form.Item>

            {/* 2. Адрес видеопотока */}
            <Form.Item
              name="address"
              label={t('videoStreams.fAddress')}
              rules={[{ required: true, whitespace: true, message: t('videoStreams.reqMsg') }]}
            >
              <Input maxLength={500} showCount placeholder={t('videoStreams.phAddress')} />
            </Form.Item>

            {/* 3. Местоположение */}
            <Form.Item
              name="location"
              label={t('videoStreams.fLocation')}
              extra={t('videoStreams.hintLocation')}
              rules={[{ required: true, whitespace: true, message: t('videoStreams.reqMsg') }]}
            >
              <Input maxLength={250} showCount placeholder={t('videoStreams.phLocation')} />
            </Form.Item>

            {/* 4. Видеофайл (RQ.21.04) */}
            <Form.Item
              name="video"
              label={t('videoStreams.fFile')}
              extra={t('videoStreams.hintFile')}
              valuePropName="fileList"
              getValueFromEvent={normFile}
              rules={[
                {
                  required: true,
                  validator: (_, value) =>
                    value && value.length
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('videoStreams.reqMsgFile'))),
                },
              ]}
            >
              <Upload
                accept=".mp4,video/*"
                maxCount={1}
                beforeUpload={() => false} // не загружать сразу — файл уходит вместе с формой
              >
                <Button icon={<UploadOutlined />}>{t('videoStreams.pick')}</Button>
              </Upload>
            </Form.Item>

            {/* 5–6. Сроки (необязательные, RQ.21.05) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Form.Item
                name="plan_date"
                label={t('videoStreams.fPlan')}
                extra={t('videoStreams.optional')}
              >
                <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                name="fact_date"
                label={t('videoStreams.fFact')}
                extra={t('videoStreams.optional')}
              >
                <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </div>
          </Form>
        </Modal>
      </div>
    </BasicLayout>
  );
}
